import { useEffect } from "react"
import { z } from "zod"

import { Form, useZodForm } from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"

import { MetaInfo } from "@/components/meta-info"
import { toast } from "@/components/toast"
import { api } from "@/lib/api"
import { captureError } from "@/lib/capture-error"
import { useTrial } from "@/lib/useTrial"
import {
	mutateConfirmed,
	MutationUnconfirmedError,
	useZero,
	useZeroWorkspace,
} from "@/lib/zero"
import { SettingsHeader } from "../settings-header"
import { useFocusAiLimitField } from "./ai-limit-focus"
import { AiPausedBanner } from "./ai-paused-banner"
import { AiTokenUsageSection } from "./ai-token-usage-section"
import { AiUsageSection } from "./ai-usage-section"
import { useAddPaymentMethod } from "./use-add-payment-method"

const AiUsageFormSchema = z.object({
	payPerUseEnabled: z.boolean(),
	monthlyLimit: z.number().int().min(0).nullable(),
})

export function AiUsagePage() {
	const workspace = useZeroWorkspace()
	const zero = useZero()
	const ctx = api.useUtils()
	const { data: pricing } = api.workspace.aiResolutionPricing.useQuery()
	const addPaymentMethod = useAddPaymentMethod()
	const { hasPaymentMethod, daysLeft } = useTrial()

	const form = useZodForm({
		schema: AiUsageFormSchema,
		defaultValues: {
			payPerUseEnabled: workspace?.aiPayPerUseEnabled ?? false,
			monthlyLimit: workspace?.aiResolutionMonthlyLimit ?? null,
		},
	})

	const storedPayPerUse = workspace?.aiPayPerUseEnabled ?? false
	const storedMonthlyLimit = workspace?.aiResolutionMonthlyLimit ?? null

	// The form follows the stored row for as long as the page is open. A one-shot
	// reset let a write that never landed keep showing its value, so the page
	// reported a cap the workspace did not have.
	useEffect(() => {
		if (!workspace) return
		const current = form.getValues()
		if (
			current.payPerUseEnabled === storedPayPerUse &&
			current.monthlyLimit === storedMonthlyLimit
		) {
			return
		}
		form.reset({
			payPerUseEnabled: storedPayPerUse,
			monthlyLimit: storedMonthlyLimit,
		})
	}, [workspace, form, storedPayPerUse, storedMonthlyLimit])

	const persist = async (values: {
		payPerUseEnabled: boolean
		monthlyLimit: number | null
	}) => {
		if (!workspace) return
		try {
			// The cap decides what gets invoiced, so it counts only once the server
			// has it, not when the local copy does.
			await mutateConfirmed(
				zero.mutate(
					mutators.workspaces.update({
						id: workspace.id,
						aiPayPerUseEnabled: values.payPerUseEnabled,
						aiResolutionMonthlyLimit: values.payPerUseEnabled
							? values.monthlyLimit
							: null,
					}),
				),
			)
			await ctx.workspace.aiResolutionPricing.invalidate()
		} catch (error) {
			captureError(error, {
				domain: "ai-chat",
				action: "update ai usage settings",
				workspaceId: workspace.id,
			})
			// The form follows the stored row, so it snaps back on its own. Say so,
			// or a rejected cap looks like a cap that saved and then vanished.
			toast.error({
				title: "Your AI spend limits did not save",
				description:
					error instanceof MutationUnconfirmedError
						? "The server has not confirmed your cap yet. Reload the page to see what is saved."
						: error instanceof Error
							? error.message
							: "The page is showing the stored values again. Set the limit once more.",
			})
		}
	}

	useFocusAiLimitField(!!workspace)

	if (!workspace) return null

	return (
		<>
			<MetaInfo title="Usage Settings" />

			<SettingsHeader
				title="Usage"
				subtitle="Track resolution and token usage, and set your spend limits."
			/>

			<Form
				className="flex flex-col gap-4"
				form={form}
				onSubmit={() => undefined}
			>
				<AiUsageSection
					notice={<AiPausedBanner />}
					form={form}
					pricing={pricing}
					onPersist={persist}
					onAddPaymentMethod={() => void addPaymentMethod()}
					hasPaymentMethod={hasPaymentMethod}
					trialDaysLeft={daysLeft}
				/>
			</Form>

			<AiTokenUsageSection />
		</>
	)
}
